/**
 * Custom Product Carousel — Standalone JS
 * Web component for carousel navigation, pagination dots + size selector drawer
 */

if (!customElements.get('cpc-carousel')) {
  class CPCCarousel extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.prevBtn = null;
      this.nextBtn = null;
      this.dots = [];
      this.isScrollable = false;
    }

    connectedCallback() {
      this.track = this.querySelector('.cpc-track');
      const section = this.closest('.cpc-section');
      if (!section) return;

      this.prevBtn = section.querySelector('.cpc-nav-btn--prev');
      this.nextBtn = section.querySelector('.cpc-nav-btn--next');
      this.dots = Array.from(section.querySelectorAll('.cpc-dot'));

      if (!this.track) return;

      this.isScrollable = this.track.classList.contains('cpc-track--scrollable');

      if (this.isScrollable) {
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scroll('prev'));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scroll('next'));

        // Dot click — jump to that page
        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            const pageIndex = parseInt(dot.dataset.index, 10);
            this.goToPage(pageIndex);
          });
        });

        this.track.addEventListener('scroll', () => this.updateState(), { passive: true });
        // Force prev disabled immediately
        if (this.prevBtn) {
          this.prevBtn.disabled = true;
          this.prevBtn.setAttribute('aria-disabled', 'true');
        }
        // Also run updateState after layout settles
        setTimeout(() => {
          this.track.scrollLeft = 0;
          this.updateState();
        }, 100);
      }
    }

    getPageStep() {
      const cards = this.track.querySelectorAll('.cpc-card');
      if (!cards.length) return 0;
      const cardWidth = cards[0].offsetWidth;
      const gap = parseInt(getComputedStyle(this.track).gap) || 0;
      // 1 page = 2 cards
      return (cardWidth + gap) * 2;
    }

    scroll(direction) {
      if (!this.track) return;
      const step = this.getPageStep();
      if (!step) return;

      this.track.scrollBy({
        left: direction === 'next' ? step : -step,
        behavior: 'smooth'
      });
    }

    goToPage(pageIndex) {
      if (!this.track) return;
      const step = this.getPageStep();
      if (!step) return;

      this.track.scrollTo({
        left: step * pageIndex,
        behavior: 'smooth'
      });
    }

    getCurrentPage() {
      const step = this.getPageStep();
      if (!step) return 0;
      return Math.round(this.track.scrollLeft / step);
    }

    updateState() {
      if (!this.track) return;

      const scrollLeft = this.track.scrollLeft;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const step = this.getPageStep();
      // Consider "at start" if less than a quarter page has been scrolled
      const threshold = step ? step * 0.25 : 5;
      const atStart = scrollLeft < threshold;
      const atEnd = scrollLeft >= maxScroll - threshold;

      // Update arrow disabled states
      if (this.prevBtn) {
        this.prevBtn.disabled = atStart;
        this.prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = atEnd;
        this.nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
      }

      // Update active dot
      const currentPage = this.getCurrentPage();
      this.dots.forEach((dot, i) => {
        const isActive = i === currentPage;
        dot.classList.toggle('cpc-dot--active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }
  }

  customElements.define('cpc-carousel', CPCCarousel);
}

/* Size selector drawer logic — scoped to .cpc-section */
document.addEventListener('DOMContentLoaded', () => {
  let touchStartY = 0;
  let touchMoved = false;

  document.addEventListener('touchstart', (e) => {
    touchStartY = e.touches[0].clientY;
    touchMoved = false;
  }, { passive: true });

  document.addEventListener('touchmove', (e) => {
    if (Math.abs(e.touches[0].clientY - touchStartY) > 10) {
      touchMoved = true;
    }
  }, { passive: true });

  document.addEventListener('click', (event) => {
    if (touchMoved) return;

    const trigger = event.target.closest('.cpc-atc-trigger');
    if (!trigger) {
      // Close any open drawers in cpc-section when clicking outside
      document.querySelectorAll('.cpc-section .cpc-atc-drawer--open').forEach((d) => {
        d.classList.remove('cpc-atc-drawer--open');
        const t = d.closest('.cpc-atc-wrap')?.querySelector('.cpc-atc-trigger');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
      return;
    }

    const wrap = trigger.closest('.cpc-atc-wrap');
    if (!wrap) return;

    const drawer = wrap.querySelector('.cpc-atc-drawer');
    if (!drawer) return;

    // Close all other drawers in cpc-sections
    document.querySelectorAll('.cpc-section .cpc-atc-drawer--open').forEach((d) => {
      if (d !== drawer) {
        d.classList.remove('cpc-atc-drawer--open');
        const otherTrigger = d.closest('.cpc-atc-wrap')?.querySelector('.cpc-atc-trigger');
        if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
      }
    });

    drawer.classList.toggle('cpc-atc-drawer--open');

    // Update aria-expanded on trigger
    const isOpen = drawer.classList.contains('cpc-atc-drawer--open');
    trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  });
});